import React, { Fragment, useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { normalizeAddress } from '@utils/normalize/normalizeAddress';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { SSAP_JSON_RESIDENCY_PROPS } from '@constants/ssap/miscellaneous/miscellaneous';
import { ConditionalRendering } from '@components/common/conditional-rendering';


export const residencyDependency = (residencyInfo = {}, name) => {
    const { stateResident, planToMakeHome, movedInDueToWork, movedRecently } = residencyInfo;

    switch (name) {
        case SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY:
            return stateResident && (planToMakeHome || movedInDueToWork);
        case SSAP_JSON_RESIDENCY_PROPS.MOVE_IN_DATE:
            return movedRecently;
        default:
            return true;
    }
};


/**
 *
 * @param {Object} props
 * @returns {React.Component} = 'Living separately with Head of household' Page
 *
 */
const Page2_15 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                householdMember: householdMembers = []
            }
        },
        form,
        environment: {
            localDates: { serverDate }
        }
    } = props;

    const { formName, pageName } = props;

    const primaryContact = householdMemberTools.getPrimaryContact(householdMembers);

    const {
        householdContact: {
            homeAddress,
        },
    } = primaryContact;

    const {
        isFormSubmitted,
        isEligiblyForSave
    } = preventerTools.provideObject(form[formName]);

    useEffect(() => {

        const newState = { ...data };

        newState.taxHousehold.householdMember =
            newState.taxHousehold.householdMember.map(hhm => {
                const {
                    applyingForCoverageIndicator,
                    householdContact: {
                        residencyInfo
                    },
                } = hhm;
                const {
                    stateResident = null,
                    planToMakeHome = null,
                    movedInDueToWork = null,
                    movedRecently = null,
                    moveInDate = null,
                    visitingForPersonalReason = null,
                } = residencyInfo ?? {};

                return ({
                    ...hhm,
                    householdContact: {
                        ...hhm.householdContact,
                        residencyInfo: {
                            stateResident: applyingForCoverageIndicator ? stateResident : null,
                            planToMakeHome: applyingForCoverageIndicator ? planToMakeHome : null,
                            movedInDueToWork: applyingForCoverageIndicator ? movedInDueToWork : null,
                            movedRecently: applyingForCoverageIndicator && stateResident ? movedRecently : null,
                            moveInDate: applyingForCoverageIndicator && stateResident ? moveInDate : null,
                            visitingForPersonalReason: applyingForCoverageIndicator ? visitingForPersonalReason : null,
                        }
                    }
                });
            });

        props.actions.changeData(newState);
    }, []);

    const onFieldChangeForResidency = (...args) => {

        const newState = { ...data };
        const { indexInArray, name, newData, value } = parseFieldChangeArgs(args);

        let residencyInfo = newData.householdContact.residencyInfo ?? {};
        residencyInfo[name] = value;

        switch (name) {
            case SSAP_JSON_RESIDENCY_PROPS.STATE_RESIDENT:
                if (value === false) {
                    residencyInfo = {
                        ...residencyInfo,
                        [SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY]: null,
                        [SSAP_JSON_RESIDENCY_PROPS.MOVE_IN_DATE]: null
                    };
                }
                //fall through

            case SSAP_JSON_RESIDENCY_PROPS.PLAN_TO_MAKE_HOME:
            case SSAP_JSON_RESIDENCY_PROPS.MOVED_IN_DUE_TO_WORK:
                if (!residencyInfo[SSAP_JSON_RESIDENCY_PROPS.PLAN_TO_MAKE_HOME] && !residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVED_IN_DUE_TO_WORK]) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY] = null;
                }
                // fall through

            case SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY:
                if (!residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY]) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVE_IN_DATE] = null;
                }
                //fall through

            default:
                if (residencyInfo[SSAP_JSON_RESIDENCY_PROPS.STATE_RESIDENT] === null) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.STATE_RESIDENT] = false;
                }
                if (residencyInfo[SSAP_JSON_RESIDENCY_PROPS.PLAN_TO_MAKE_HOME] === null) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.PLAN_TO_MAKE_HOME] = false;
                }
                if (residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVED_IN_DUE_TO_WORK] === null) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.MOVED_IN_DUE_TO_WORK] = false;
                }
                if (residencyInfo[SSAP_JSON_RESIDENCY_PROPS.VISITING_REASON] === null) {
                    residencyInfo[SSAP_JSON_RESIDENCY_PROPS.VISITING_REASON] = false;
                }
                break;

        }

        newData.householdContact.residencyInfo = residencyInfo;
        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChangeForNoneOfAbove = (...args) => {

        const newState = { ...data };
        const { indexInArray, newData } = parseFieldChangeArgs(args);

        newData.householdContact.residencyInfo = {
            stateResident: false,
            planToMakeHome: false,
            movedInDueToWork: false,
            movedRecently: null,
            moveInDate: null,
            visitingForPersonalReason: false
        };
        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const getHouseholdMembers = householdMembers => {
        const members = [];

        householdMembers.forEach((member, i) => {
            const {
                applicantGuid,
                applyingForCoverageIndicator,
                householdContact: {
                    residencyInfo
                },
                name
            } = member;

            const {
                stateResident = null,
                planToMakeHome = null,
                movedInDueToWork = null,
                movedRecently = null,
                moveInDate = null,
                visitingForPersonalReason = null,
            } = residencyInfo ?? {};

            let noReason = stateResident === false && planToMakeHome === false && movedInDueToWork === false && visitingForPersonalReason === false;

            if (applyingForCoverageIndicator) {
                members.push(
                    <li key={applicantGuid} className={i !== 0 ? 'padding-t-20' : ''}>
                        {getValidationErrorMessage(residencyInfo)}
                        <span className='usa-sr-only' >
                            <FormattedMessage id="ssap.input.label.residencyHelpText" values={{ name: normalizeNameOfPerson(name) }} />
                        </span>
                        <p className='required-true' >
                            <FormattedMessage
                                id='common.input.label.non_internationalized_text'
                                values={{
                                    text: normalizeNameOfPerson(name)
                                }}
                            />
                        </p>
                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.stateResident',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            }}
                            inputData={{
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'stateResident',
                                checked: stateResident,
                                value: stateResident,
                                currentData: member,
                                fields: ['householdContact', 'residencyInfo'],
                                isInputStandalone: true
                            }}
                            inputActions={{
                                onFieldChange: onFieldChangeForResidency
                            }}
                            indexInArray={i}
                        />
                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.planToMakeHome',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            }}
                            inputData={{
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'planToMakeHome',
                                checked: planToMakeHome,
                                value: planToMakeHome,
                                currentData: member,
                                fields: ['householdContact', 'residencyInfo'],
                                isInputStandalone: true
                            }}
                            inputActions={{
                                onFieldChange: onFieldChangeForResidency
                            }}
                            indexInArray={i}
                        />

                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.movedInDueToWork',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            }}
                            inputData={{
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'movedInDueToWork',
                                checked: movedInDueToWork,
                                value: movedInDueToWork,
                                currentData: member,
                                fields: ['householdContact', 'residencyInfo'],
                                isInputStandalone: true
                            }}
                            inputActions={{
                                onFieldChange: onFieldChangeForResidency
                            }}
                            indexInArray={i}
                        />

                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.visiting',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            }}
                            inputData={{
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'visitingForPersonalReason',
                                checked: visitingForPersonalReason,
                                value: visitingForPersonalReason,
                                currentData: member,
                                fields: ['householdContact', 'residencyInfo'],
                                isInputStandalone: true
                            }}
                            inputActions={{
                                onFieldChange: onFieldChangeForResidency
                            }}
                            indexInArray={i}
                        />

                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.none_of_above'
                            }}
                            inputData={{
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'noReason',
                                classes: [],
                                checked: noReason,
                                value: noReason,
                                currentData: member,
                                fields: ['householdContact', 'residencyInfo'],
                                isInputStandalone: true
                            }}
                            inputActions={{
                                onFieldChange: onFieldChangeForNoneOfAbove
                            }}
                            indexInArray={i}
                        />

                        <ConditionalRendering isTrue={stateResident}>
                            <ConditionalRendering isTrue={residencyDependency(residencyInfo, SSAP_JSON_RESIDENCY_PROPS.MOVED_RECENTLY)}>
                                <UniversalInput
                                    legend={{
                                        legendText: 'ssap.input.legend.movedRecently'
                                    }}
                                    label={{
                                        labelClass: ['grid-row'],
                                        ulClass: [],
                                        fieldSetClass: ['d-flex align-items-center'],
                                        showRequiredIcon: true
                                    }}

                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'movedRecently',
                                        helperClasses: [],
                                        value: movedRecently,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: member,
                                        fields: ['householdContact', 'residencyInfo'],
                                        isInputStandalone: true
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: onFieldChangeForResidency
                                    }}
                                    indexInArray={i}
                                />
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={residencyDependency(residencyInfo, SSAP_JSON_RESIDENCY_PROPS.MOVE_IN_DATE)}>
                                <UniversalInput
                                    legend={{
                                        legendText: 'ssap.input.label.moveInDate',
                                    }}
                                    label={{
                                        text: 'ssap.input.label.moveInDate',
                                        labelClass: ['grid-row p-0'],
                                    }}
                                    containerClass={['mt-0 pb-3']}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DATE_PICKER,
                                        name: 'moveInDate',
                                        helperClasses: [],
                                        value: moveInDate,
                                        valuesToMatch: [serverDate, 90],
                                        validationType: VALIDATION_TYPES.LAST_N_DAYS,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MOVE_IN_DATE,
                                        currentData: member,
                                        fields: ['householdContact', 'residencyInfo'],
                                        isInputStandalone: true
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: onFieldChangeForResidency
                                    }}
                                    indexInArray={i}
                                />
                            </ConditionalRendering>
                        </ConditionalRendering>

                    </li>
                );
            }
        });

        return members;
    };

    const checkResidencyReasonSelected = (residencyInfo) => {
        const {
            stateResident = null,
            planToMakeHome = null,
            movedInDueToWork = null,
            visitingForPersonalReason = null,
        } = residencyInfo ?? {};

        return stateResident !== null && planToMakeHome !== null && movedInDueToWork !== null && visitingForPersonalReason !== null;
    };

    const atLeastOneReasonSelectedOrNotApplying = useMemo(
        () => householdMembers.every(hhm => !hhm.applyingForCoverageIndicator || checkResidencyReasonSelected(hhm.householdContact.residencyInfo)),
        [householdMembers.map(hhm => !hhm.applyingForCoverageIndicator || checkResidencyReasonSelected(hhm.householdContact.residencyInfo))]
    );

    const isFormValid = useMemo(() => atLeastOneReasonSelectedOrNotApplying, [atLeastOneReasonSelectedOrNotApplying]);

    const getValidationErrorMessage = (residencyInfo) => {
        let messageText = VALIDATION_ERROR_MESSAGES.ANY_VALUE;
        return (
            <ValidationErrorMessage
                isVisible={isFormSubmitted && !isEligiblyForSave && !checkResidencyReasonSelected(residencyInfo)}
                messageText={messageText}
                errorClasses={['margin-l-0']}
            />
        );
    };

    return (
        <Fragment>
            <div className="fade-in" id="fhAdditionalInfo">
                <div className="subsection">
                    <Form topLevelValidity={isFormValid} {...{ formName, pageName }}>
                        <fieldset className="usa-fieldset">
                            <legend className="required-true" data-testid="residency_reasons_legend">
                                <FormattedMessage
                                    id="ssap.input.legend.residency_reasons"
                                    values={{
                                        homeAddress: <strong>{normalizeAddress(homeAddress, undefined, true)}</strong>,
                                        strong: chunks => <strong>{chunks}</strong>,
                                    }}
                                />
                            </legend>
                            <AlertInfo id='ssap.start.addresses.residency_reasons_alert' />
                            <ul className='usa-list usa-list--unstyled' >
                                {
                                    getHouseholdMembers(householdMembers)
                                }
                            </ul>
                        </fieldset>
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    data: state.ssap.data,
    form: state.ssap.form
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_15);
